import React, { useEffect, useState, Component } from 'react';
import { EyeIcon, Loader2, InfoIcon, PlusCircleIcon } from 'lucide-react';
import { FormSection, TextField, TextAreaField, FileUploadField, SelectField } from '../FormComponents';
import { DataTable } from '../DataTable';
import { StatusBadge } from '../StatusBadge';
import { SupplierViewModal } from './SupplierViewModal';
import { RFPSupplier } from '../../types';
import { mockSuppliers } from '../../data/mockData';
// Fallback EOI suppliers if none provided via props
const defaultEOISuppliers = [{
  id: '1',
  supplierId: 'SUP-001',
  supplierName: 'TechCorp Solutions'
}, {
  id: '2',
  supplierId: 'SUP-002',
  supplierName: 'Global Logistics Inc'
}];
interface RFPPanelProps {
  onStatusChange?: (status: string) => void;
  eoiSkipped?: boolean; // undefined = not open-tender mode, true = EOI skipped, false = EOI was used
  eoiSuppliers?: {
    id: string;
    supplierId: string;
    supplierName: string;
  }[]; // Suppliers shortlisted from EOI
  maxSuppliers?: number; // Limit number of suppliers (e.g., 1 for Direct Purchase)
}
export function RFPPanel({
  onStatusChange,
  eoiSkipped,
  eoiSuppliers,
  maxSuppliers
}: RFPPanelProps) {
  const [formData, setFormData] = useState({
    submissionDeadline: '',
    evaluationCriteria: '',
    selectedSupplier: '',
    remarks: '',
    status: 'Draft'
  });
  const [suppliers, setSuppliers] = useState<RFPSupplier[]>([]);
  const [selectedSupplierForModal, setSelectedSupplierForModal] = useState<RFPSupplier | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReadOnly = formData.status !== 'Draft';
  // Determine supplier source
  const isOpenTender = eoiSkipped !== undefined;
  const useEOISuppliers = isOpenTender && eoiSkipped === false;
  const resolvedEOISuppliers = eoiSuppliers || defaultEOISuppliers;
  const hasReachedLimit = maxSuppliers !== undefined && suppliers.length >= maxSuppliers;
  // Build dropdown options based on supplier source
  const getSupplierOptions = () => {
    if (useEOISuppliers) {
      // Load from approved EOI list (passed from EOI data or fallback)
      return resolvedEOISuppliers.map(s => ({
        value: s.supplierId,
        label: `${s.supplierName} (EOI Approved)`
      }));
    }
    // Default: Load from Supplier Management (Active suppliers only)
    return mockSuppliers.filter(s => s.status === 'Active').map(s => ({
      value: s.code,
      label: `${s.name} (${s.code})`
    }));
  };
  const supplierOptions = getSupplierOptions();
  // Handle adding a supplier to the evaluation table
  const handleAddSupplier = () => {
    if (!formData.selectedSupplier) return;
    // Check if already added
    const alreadyAdded = suppliers.some(s => s.supplierId === formData.selectedSupplier);
    if (alreadyAdded) return;
    // Find supplier info
    const selectedOption = supplierOptions.find(o => o.value === formData.selectedSupplier);
    if (!selectedOption) return;
    const supplierName = selectedOption.label.replace(/ \(.*\)$/, '');
    const newSupplier: RFPSupplier = {
      id: Date.now().toString(),
      supplierId: formData.selectedSupplier,
      supplierName: supplierName,
      invitationDate: new Date().toISOString().split('T')[0],
      submissionStatus: 'Pending',
      responseStatus: 'Pending',
      documents: []
    };
    setSuppliers(prev => [...prev, newSupplier]);
    setFormData({
      ...formData,
      selectedSupplier: ''
    });
  };
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(formData.status);
    }
  }, [formData.status, onStatusChange]);
  const columns = [{
    key: 'supplierName',
    label: 'Supplier Name'
  }, {
    key: 'invitationDate',
    label: 'Invitation Date'
  }, {
    key: 'submissionStatus',
    label: 'Submission',
    render: (item: RFPSupplier) => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.submissionStatus === 'Submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-371">
          {item.submissionStatus}
        </span>
  }, {
    key: 'technicalScore',
    label: 'Tech Score',
    render: (item: RFPSupplier) => <input type="number" min="0" max="100" className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 text-center disabled:bg-gray-100" value={item.technicalScore ?? ''} disabled={formData.status !== 'Published'} onChange={e => {
      const val = e.target.value === '' ? undefined : Number(e.target.value);
      setSuppliers(prev => prev.map(s => s.id === item.id ? {
        ...s,
        technicalScore: val,
        totalScore: val !== undefined && s.financialScore !== undefined ? (val + s.financialScore) / 2 : undefined
      } : s));
    }} placeholder="0-100" data-id="element-372" />
  }, {
    key: 'financialScore',
    label: 'Fin Score',
    render: (item: RFPSupplier) => <input type="number" min="0" max="100" className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 text-center disabled:bg-gray-100" value={item.financialScore ?? ''} disabled={formData.status !== 'Published'} onChange={e => {
      const val = e.target.value === '' ? undefined : Number(e.target.value);
      setSuppliers(prev => prev.map(s => s.id === item.id ? {
        ...s,
        financialScore: val,
        totalScore: s.technicalScore !== undefined && val !== undefined ? (s.technicalScore + val) / 2 : undefined
      } : s));
    }} placeholder="0-100" data-id="element-373" />
  }, {
    key: 'totalScore',
    label: 'Total Score',
    render: (item: RFPSupplier) => <span className="font-medium text-indigo-600" data-id="element-374">
          {item.totalScore != null ? item.totalScore.toFixed(1) : '-'}
        </span>
  }, {
    key: 'responseStatus',
    label: 'Status',
    render: (item: RFPSupplier) => <StatusBadge status={item.responseStatus} data-id="element-375" />
  }];
  const handleViewSupplier = (supplier: RFPSupplier) => {
    setSelectedSupplierForModal(supplier);
    setIsModalOpen(true);
  };
  const handleSaveScores = (id: string, techScore: number, finScore: number) => {
    setSuppliers(prev => prev.map(s => {
      if (s.id === id) {
        return {
          ...s,
          technicalScore: techScore,
          financialScore: finScore,
          totalScore: (techScore + finScore) / 2,
          responseStatus: 'Evaluated'
        };
      }
      return s;
    }));
  };
  const handleSubmit = () => {
    setIsSubmitting(true);
    setFormData({
      ...formData,
      status: 'Pending'
    });
    // Simulate approval process
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        status: 'Approved'
      }));
      setIsSubmitting(false);
    }, 1500);
  };
  const handlePublish = () => {
    setFormData({
      ...formData,
      status: 'Published'
    });
  };
  const getSupplierSourceLabel = () => {
    if (useEOISuppliers) return 'Approved EOI Suppliers';
    return 'Supplier Management (Active)';
  };
  return <div className="space-y-6" data-id="element-376">
      <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-377">
        <FormSection title="Request for Proposal (RFP) Details" data-id="element-378">
          <TextField label="RFP Submission Deadline" type="date" value={formData.submissionDeadline} onChange={e => setFormData({
          ...formData,
          submissionDeadline: e.target.value
        })} required disabled={isReadOnly} data-id="element-379" />

          {/* Supplier Source Info Banner */}
          <div className="md:col-span-2" data-id="element-380">
            <div className={`p-3 rounded-md flex items-start text-sm ${useEOISuppliers ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`} data-id="element-381">
              <InfoIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" data-id="element-382" />
              <span data-id="element-383">
                {useEOISuppliers ? 'Suppliers are loaded from the approved EOI list. Only suppliers who passed the EOI evaluation are available for selection.' : maxSuppliers === 1 ? 'Direct Purchase: Select one supplier from the Supplier Management list.' : 'Suppliers are loaded from the Supplier Management master list (Active suppliers only).'}
              </span>
            </div>
          </div>

          {/* Supplier Selection with Add button */}
          <div className="md:col-span-2" data-id="element-384">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-385">
              Select Supplier(s) from {getSupplierSourceLabel()}
              {maxSuppliers !== undefined && <span className="text-gray-400 font-normal ml-1" data-id="element-386">
                  (max {maxSuppliers})
                </span>}
            </label>
            <div className="flex gap-2" data-id="element-387">
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 bg-white disabled:bg-gray-100" value={formData.selectedSupplier} onChange={e => setFormData({
              ...formData,
              selectedSupplier: e.target.value
            })} disabled={isReadOnly || hasReachedLimit} data-id="element-388">
                <option value="" data-id="element-389">Select a supplier...</option>
                {supplierOptions.map(opt => {
                const alreadyAdded = suppliers.some(s => s.supplierId === opt.value);
                return <option key={opt.value} value={opt.value} disabled={alreadyAdded} data-id="element-390">
                      {opt.label} {alreadyAdded ? '(Already Added)' : ''}
                    </option>;
              })}
              </select>
              <button type="button" onClick={handleAddSupplier} disabled={!formData.selectedSupplier || isReadOnly || hasReachedLimit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap" data-id="element-391">
                <PlusCircleIcon className="h-4 w-4 mr-1" data-id="element-392" />
                Add
              </button>
            </div>
            {suppliers.length > 0 && <p className="mt-1 text-xs text-gray-500" data-id="element-393">
                {suppliers.length} supplier(s) added to evaluation
                {hasReachedLimit && ' — maximum reached'}
              </p>}
          </div>

          <div className="md:col-span-2" data-id="element-394">
            <TextAreaField label="RFP Evaluation Criteria" value={formData.evaluationCriteria} onChange={e => setFormData({
            ...formData,
            evaluationCriteria: e.target.value
          })} required rows={3} placeholder="e.g., price, delivery timeline, compliance with specifications" disabled={isReadOnly} data-id="element-395" />
          </div>
          <FileUploadField label="RFP Attachment" disabled={isReadOnly} data-id="element-396" />
          <div className="md:col-span-2" data-id="element-397">
            <TextAreaField label="Remarks" value={formData.remarks} onChange={e => setFormData({
            ...formData,
            remarks: e.target.value
          })} rows={2} disabled={isReadOnly} data-id="element-398" />
          </div>
        </FormSection>

        <div className="mt-8" data-id="element-399">
          <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-400">
            Selected Suppliers & Evaluation
          </h3>
          <div className="h-64" data-id="element-401">
            <DataTable columns={columns} data={suppliers} keyExtractor={item => item.id} actions={item => <div className="flex items-center justify-end space-x-3" data-id="element-403">
                  <button type="button" onClick={() => handleViewSupplier(item)} className="text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-404">
                    <EyeIcon className="h-4 w-4 mr-1" data-id="element-405" /> View
                  </button>
                </div>} data-id="element-402" />
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-gray-200 flex items-center justify-between" data-id="element-406">
          <div className="flex items-center" data-id="element-407">
            <span className="text-sm font-medium text-gray-500 mr-2" data-id="element-408">
              Status:
            </span>
            <StatusBadge status={formData.status} data-id="element-409" />
          </div>
          <div className="flex space-x-3" data-id="element-410">
            {formData.status === 'Draft' && <>
                <button type="button" onClick={() => setFormData({
              ...formData,
              status: 'Draft'
            })} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-411">
                  Save as Draft
                </button>
                <button type="button" onClick={handleSubmit} disabled={isSubmitting} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50" data-id="element-412">
                  {isSubmitting && <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" data-id="element-413" />}
                  Submit for Approval
                </button>
              </>}
            {formData.status === 'Approved' && <button type="button" onClick={handlePublish} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700" data-id="element-414">
                Publish RFP
              </button>}
          </div>
        </div>
      </div>

      <SupplierViewModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} supplier={selectedSupplierForModal} type="RFP" onSaveScores={handleSaveScores} data-id="element-415" />
    </div>;
}